import type { NewReportKey, NewReportSnapshot } from './report-runtime';

/** Stable report destinations for Reports Hub. */
export const REPORT_KEYS = [
  'service_job_detail',
  'cancelled_services',
  'missed_services',
  'daily_service_frequency',
  'service_frequency_today',
  'daily_driver_service',
  'driver_assignment_detail',
  'service_users',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

/** Unfinished reports stay visible in navigation but cannot be opened. */
export const REPORT_AVAILABILITIES = ['AVAILABLE', 'COMING_SOON'] as const;
export type ReportAvailability = (typeof REPORT_AVAILABILITIES)[number];

export const REPORT_MINIMUM_ROLES = ['OFFICER', 'MANAGER', 'ADMIN'] as const;
export type ReportMinimumRole = (typeof REPORT_MINIMUM_ROLES)[number];

export interface ReportAccessPolicy {
  reportKey: ReportKey;
  minimumRole: ReportMinimumRole;
  source: 'SYSTEM_DEFAULT' | 'PROVIDER';
}

export interface ReportDefinition {
  key: ReportKey;
  availability: ReportAvailability;
  schemaVersion: number | null;
}

export interface ReportRegistryResponse {
  /** Creation gate: compatible workers must be deployed before enabling new exports. */
  reportHubExportEnabled?: boolean;
  /** Absent on older APIs means exception exports are unavailable. */
  locationExceptionExportEnabled?: boolean;
  providerId: string;
  data: Array<
    ReportDefinition & {
      minimumRole: ReportMinimumRole;
      policySource: ReportAccessPolicy['source'];
      allowed: boolean;
    }
  >;
}

export interface ReportFilterOption {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
}

export interface ReportFilterOptionsResponse {
  data: ReportFilterOption[];
  total: number;
  nextOffset: number | null;
}

/**
 * Product readiness, not a feature flag. A report becomes AVAILABLE only once
 * its production contract, API, and UI adapter have shipped together.
 */
export const REPORT_DEFINITIONS: readonly ReportDefinition[] = REPORT_KEYS.map((key) => ({
  key,
  availability: 'AVAILABLE',
  schemaVersion: key === 'service_job_detail' ? 3 : 1,
}));

export const SERVICE_JOB_DETAIL_FARE_STATES = ['NONE', 'PROVISIONAL', 'FINAL', 'CANCELLED_SNAPSHOT'] as const;
export type ServiceJobDetailFareState = (typeof SERVICE_JOB_DETAIL_FARE_STATES)[number];

export const SERVICE_JOB_DETAIL_QUOTE_PROVENANCES = ['BOOKING_SNAPSHOT', 'UNAVAILABLE'] as const;
export type ServiceJobDetailQuoteProvenance = (typeof SERVICE_JOB_DETAIL_QUOTE_PROVENANCES)[number];

export const REPORT_PAYMENT_STATES = [
  'FREE',
  'FINANCE_REVIEW',
  'NOT_BILLABLE',
  'UNBILLED',
  'OUTSTANDING',
  'PARTIAL',
  'SETTLED',
] as const;
export type ReportPaymentState = (typeof REPORT_PAYMENT_STATES)[number];

export interface ServiceJobDetailPaymentFacts {
  status: import('./enums').PaymentStatus;
  amountDue: number;
  amountReceived: number;
  outstanding: number;
  dueBy: string | null;
  verifiedAt: string | null;
}

export interface ServiceJobDetailFinance {
  waivedOutstandingTotal?: number;
  overageReviewStatus?: 'PENDING' | 'APPROVED' | 'WAIVED' | null;
  currency: 'THB' | null;
  fareState: ServiceJobDetailFareState;
  quoteProvenance: ServiceJobDetailQuoteProvenance;
  quotedTotal: number | null;
  currentTotal: number | null;
  finalTotal: number | null;
  fareAdjustmentDelta: number | null;
  fareBase: number | null;
  fareOverageDistance: number | null;
  fareOverageTime: number | null;
  fareSurchargeTotal: number | null;
  fareComputedAt: string | null;
  billingDistanceSource: import('./enums').BillingMetricSource | null;
  billingDurationSource: import('./enums').BillingMetricSource | null;
  billableDistanceKm: number | null;
  billableDurationMin: number | null;
  fareIsFree: boolean;
  basePayment: ServiceJobDetailPaymentFacts | null;
  overagePayment: ServiceJobDetailPaymentFacts | null;
  billedTotal: number;
  receivedTotal: number;
  completedRefundsTotal: number;
  netRetainedTotal: number;
  pendingRefundsTotal: number;
  reconciliationCashTotal: number;
  outstandingTotal: number;
  paymentState: ReportPaymentState;
  requiresFinanceReview: boolean;
}

export interface ServiceJobDetailPerson {
  id: string;
  code: string | null;
  name: string;
  phone: string;
  pictureUrl: string | null;
}

export interface ServiceJobDetailServiceArea {
  id: string;
  name: string;
}

export interface ServiceJobDetailRow {
  /** Required v2 projection; latest is ordered by event time then event ID. */
  hasLocationException: boolean;
  exceptionCount: number;
  latestExceptionAt: string | null;
  latestExceptionEvent: typeof import('./status-location').STATUS_LOCATION_EVENTS[number] | null;
  latestExceptionWarning: Exclude<
    import('./status-location').StatusLocationEvidence['warning'],
    'NONE'
  > | null;
  latestExceptionOverrideReason: import('./status-location').LocationOverrideReason | null;
  latestExceptionConfirmation: import('./status-location').StatusLocationEvidence['confirmation'] | null;
  latestExceptionSource: import('./status-location').StatusLocationEvidence['source'];
  latestExceptionAccuracyM: number | null;
  latestExceptionDistanceToTargetM: number | null;
  latestExceptionRadiusM: number | null;
  tripId: string;
  tripRef: string;
  tripStatus: import('./enums').TripStatus;
  tripLeg: import('./enums').TripLeg;
  serviceMode: import('./enums').ServiceMode;
  dispatchMode: import('./enums').TripDispatchMode | null;
  createdAt: string;
  requestedAt: string;
  serviceDate: string;
  serviceDateBasis: 'SCHEDULED_START' | 'CREATED_AT_FALLBACK';
  scheduledStart: string | null;
  scheduledEnd: string | null;
  acceptedAt: string | null;
  startedAt: string | null;
  pickedUpAt: string | null;
  completedAt: string | null;
  passenger: ServiceJobDetailPerson;
  agent: ServiceJobDetailPerson | null;
  vehicle: { id: string; plate: string } | null;
  provider: { id: string; name: string };
  serviceGroup: { id: string; name: string };
  serviceAreas: ServiceJobDetailServiceArea[];
  originLabel: string;
  destinationLabel: string;
  plannedDistanceKm: number | null;
  plannedDurationMin: number | null;
  actualDistanceKm: number | null;
  actualDurationMin: number | null;
  actualDistanceSource: import('./enums').ActualMetricSource | null;
  actualDurationSource: import('./enums').ActualMetricSource | null;
  finance: ServiceJobDetailFinance;
}

export interface ServiceJobDetailSummary {
  rowCount: number;
  fareTotalSum: number;
  billedTotalSum: number;
  receivedTotalSum: number;
  outstandingTotalSum: number;
  freeTripCount: number;
  financeReviewCount: number;
}

export interface ServiceJobDetailPreviewResponse {
  reportKey: 'service_job_detail';
  schemaVersion: 3;
  dateBasis: 'SERVICE_DATE';
  dataAsOf: string;
  page: { limit: number; offset: number; total: number; nextOffset: number | null };
  summary: ServiceJobDetailSummary;
  data: ServiceJobDetailRow[];
}

export const REPORT_EXPORT_STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED', 'EXPIRED'] as const;
export type ReportExportStatus = (typeof REPORT_EXPORT_STATUSES)[number];

export const REPORT_ALL_TIME_FROM = '1970-01-01T00:00:00.000Z';
export const REPORT_ALL_TIME_TO = '9999-12-31T23:59:59.999Z';

export function isReportAllTimeRange(range: { from: string; to: string }): boolean {
  return range.from === REPORT_ALL_TIME_FROM && range.to === REPORT_ALL_TIME_TO;
}

export interface ServiceJobDetailExportFilters {
  hasLocationException?: boolean;
  latestExceptionWarnings?: string[];
  latestExceptionOverrideReasons?: string[];
  /** Omitted only on legacy export snapshots using the original full CSV schema. */
  columns?: import('./report-columns').ServiceJobDetailColumnId[];
  locale?: import('./report-columns').ReportColumnLocale;
  from: string;
  to: string;
  providerIds: string[];
  serviceAreaIds: string[];
  serviceGroupIds: string[];
  statuses: import('./enums').TripStatus[];
  paymentStates: ReportPaymentState[];
  q: string;
  sort: 'serviceDate' | 'tripRef' | 'status' | 'createdAt';
  order: 'asc' | 'desc';
}

export type ReportExportFilters = ServiceJobDetailExportFilters | NewReportSnapshot;

interface ReportExportJobBase {
  id: string;
  status: ReportExportStatus;
  providerIds: string[];
  rowCount: number | null;
  dataAsOf: string | null;
  filename: string | null;
  failureCode: string | null;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
}

/** The discriminator preserves the report key/filter relationship for consumers. */
export type ReportExportJob =
  | (ReportExportJobBase & {
      reportKey: 'service_job_detail';
      schemaVersion: 1 | 2 | 3;
      filters: ServiceJobDetailExportFilters;
    })
  | (ReportExportJobBase & {
      reportKey: NewReportKey;
      schemaVersion: 1;
      filters: NewReportSnapshot;
    });

export interface ReportExportListResponse {
  data: ReportExportJob[];
  total: number;
  nextCursor: string | null;
}
